import {
  differenceInMinutes,
  getDeltaMsg,
  getDeltaTime,
} from '@project44/ui-utils';

import { scopedT } from './scopedT';

export type DateDiff = { text: string; color: string; tooltip: string };

/**
 * Compares dates and constructs a `DateDiff` object based on comparison.
 * Currently supporting two interfaces: with and without `plannedEndDate`. Once `plannedEndDate` is always provided
 * this function can be refactored to remove
 * @returns object
 */
export const getDateDiffDeprecated = (
  predictedDate: string,
  plannedStartDate: string,
  isPredicted?: boolean,
  plannedEndDate?: string,
): DateDiff | null => {
  const t = scopedT();

  const isEarly = differenceInMinutes(predictedDate, plannedStartDate) < 0;

  /**
   * For `isLate`:
   * Old: compare to `plannedStartDate`
   * New: compare to `plannedEndDate`
   */
  const isLate =
    differenceInMinutes(predictedDate, plannedEndDate ?? plannedStartDate) > 0;

  /**
   * For offset we compare `isEarly` to `plannedStartDate` and `isLate` to `plannedEndDate`
   * Old: compare early and late to `plannedStartDate`
   * New: when late, compare to `plannedEndDate`
   */
  const offsetMins = differenceInMinutes(
    predictedDate,
    isLate && plannedEndDate ? plannedEndDate : plannedStartDate,
  );

  // This most likely means one of our date props was invalid
  if (isNaN(offsetMins)) {
    return null;
  }

  // Text displayed in UI
  let diffDisplayText;
  let diffAmount = '';

  // Means it's early or late
  if (offsetMins !== 0) {
    const msg = getDeltaMsg(getDeltaTime(offsetMins));
    const items = msg.map(([count, unit]) => t(unit, { count }));

    diffDisplayText = items[0];
    diffAmount = items.join(' ');
  }

  let tooltipText = '';
  let text = '';
  let color = '';

  if (isLate) {
    tooltipText = t('lateByAmount', {
      diffAmount,
    });
    text = '+';
    color = '$text-danger';
    // Early
  } else if (isEarly) {
    tooltipText = t('earlyByAmount', {
      diffAmount,
    });
    text = '-';
    color = '$palette-purple-500';
  }

  return {
    text: text ? `${text}${diffDisplayText}` : '',
    color,
    tooltip: tooltipText,
  };
};

export const getDateDiff = (
  predictedDate: string,
  plannedStartDate: string,
  plannedEndDate?: string,
): DateDiff | null =>
  getDateDiffDeprecated(predictedDate, plannedStartDate, false, plannedEndDate);
